import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { Dialog } from '@/components/AppDialog'
import { useCreateStaff, useOutlets, useUpdateStaff } from '@/api/hooks'
import type { StaffMember } from '@/api/types'
import { ErrorBanner } from '@/components/common'

export const STAFF_ROLES = [
  'EVENT_MANAGER',
  'BANQUET_CAPTAIN',
  'SERVICE_STAFF',
  'CHEF',
  'ASSISTANT_CHEF',
  'LIVE_COUNTER_CHEF',
  'STEWARD',
  'CLEANER',
  'LOADER',
  'DRIVER',
  'SECURITY',
  'CASHIER',
]

const EMPLOYMENT_TYPES = ['EMPLOYEE', 'CONTRACT', 'VENDOR_SUPPLIED']

/** A plain label out of a stored code: EVENT_MANAGER becomes Event manager. */
function label(code: string) {
  const words = code.replace(/_/g, ' ').toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * Puts somebody on the staff master, or corrects what is already there.
 *
 * <p>The same form switches somebody off: there is no separate delete, here or
 * on the server - a staff record that has ever been allocated to an event is
 * history, not a row to remove, so it is marked inactive and left out of the
 * pickers instead.</p>
 */
export function StaffDialog({
  open,
  staff,
  onClose,
}: {
  open: boolean
  /** The person being corrected. Absent, a new one is added. */
  staff?: StaffMember | null
  onClose: () => void
}) {
  const create = useCreateStaff()
  const update = useUpdateStaff()
  const { data: outlets } = useOutlets()
  const editing = Boolean(staff)

  const [staffCode, setStaffCode] = useState('')
  const [fullName, setFullName] = useState('')
  const [employmentType, setEmploymentType] = useState('EMPLOYEE')
  const [primaryRole, setPrimaryRole] = useState('SERVICE_STAFF')
  const [outletId, setOutletId] = useState('')
  const [mobile, setMobile] = useState('')
  const [email, setEmail] = useState('')
  const [costPerShift, setCostPerShift] = useState('')
  const [costPerHour, setCostPerHour] = useState('')
  const [skills, setSkills] = useState('')
  const [licenceNumber, setLicenceNumber] = useState('')
  const [licenceExpiry, setLicenceExpiry] = useState('')
  const [active, setActive] = useState(true)

  useEffect(() => {
    if (!open) return
    setStaffCode(staff?.staffCode ?? '')
    setFullName(staff?.fullName ?? '')
    setEmploymentType(staff?.employmentType ?? 'EMPLOYEE')
    setPrimaryRole(staff?.primaryRole ?? 'SERVICE_STAFF')
    setOutletId(staff?.outletId ?? '')
    setMobile(staff?.mobile ?? '')
    setEmail(staff?.email ?? '')
    setCostPerShift(staff?.costPerShift != null ? String(staff.costPerShift) : '')
    setCostPerHour(staff?.costPerHour != null ? String(staff.costPerHour) : '')
    setSkills((staff?.skills ?? []).join(', '))
    setLicenceNumber(staff?.licenceNumber ?? '')
    setLicenceExpiry(staff?.licenceExpiry ?? '')
    setActive(staff?.active ?? true)
  }, [open, staff?.id])

  const submit = () => {
    const body = {
      staffCode: staffCode.trim().toUpperCase(),
      fullName: fullName.trim(),
      employmentType,
      primaryRole,
      outletId: outletId || undefined,
      mobile: mobile.trim() || undefined,
      email: email.trim() || undefined,
      costPerShift: costPerShift ? Number(costPerShift) : undefined,
      costPerHour: costPerHour ? Number(costPerHour) : undefined,
      skills: skills
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
      licenceNumber: licenceNumber.trim() || undefined,
      licenceExpiry: licenceExpiry || undefined,
      active,
    }
    if (staff) {
      update.mutate({ id: staff.id, body }, { onSuccess: onClose })
      return
    }
    create.mutate(body, { onSuccess: onClose })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `Staff ${staff?.staffCode}` : 'Add staff'}</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={create.error ?? update.error} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Staff code"
              value={staffCode}
              onChange={(event) => setStaffCode(event.target.value)}
              fullWidth
              required
              placeholder="STF-0042"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Full name"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Employment"
              value={employmentType}
              onChange={(event) => setEmploymentType(event.target.value)}
              fullWidth
            >
              {EMPLOYMENT_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {label(value)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Role"
              value={primaryRole}
              onChange={(event) => setPrimaryRole(event.target.value)}
              fullWidth
            >
              {STAFF_ROLES.map((value) => (
                <MenuItem key={value} value={value}>
                  {label(value)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Outlet"
              value={outletId}
              onChange={(event) => setOutletId(event.target.value)}
              fullWidth
              helperText="Left blank, they are offered at any outlet"
            >
              <MenuItem value="">Any outlet</MenuItem>
              {(outlets ?? []).map((outlet) => (
                <MenuItem key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Mobile"
              value={mobile}
              onChange={(event) => setMobile(event.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Skills"
              value={skills}
              onChange={(event) => setSkills(event.target.value)}
              fullWidth
              placeholder="Live counter, Flower arrangement"
              helperText="Separate more than one with a comma"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Cost per shift"
              type="number"
              value={costPerShift}
              onChange={(event) => setCostPerShift(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Cost per hour"
              type="number"
              value={costPerHour}
              onChange={(event) => setCostPerHour(event.target.value)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Licence number"
              value={licenceNumber}
              onChange={(event) => setLicenceNumber(event.target.value)}
              fullWidth
              helperText="A driver's licence, mainly"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Licence expiry"
              type="date"
              value={licenceExpiry}
              onChange={(event) => setLicenceExpiry(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {editing ? (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch checked={active} onChange={(event) => setActive(event.target.checked)} />
                }
                label={active ? 'Active - offered when staffing an event' : 'Inactive - kept on record, not offered'}
              />
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!staffCode.trim() || !fullName.trim() || create.isPending || update.isPending}
        >
          {editing ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
